/**
 * Helper questions for the criteria that depend on data. Each returns a SUGGESTED level plus the reason; the inspector
 * always confirms or changes it. Thresholds are taken from the report (docs/SOURCE-FINDINGS.md section 1).
 */
import type { ScoredLevel } from '../api/types'

export interface Suggestion {
  level: ScoredLevel
  because: string
}

/** Criteria 3 and 4: "medium: top-up about every 2 weeks; critical: more often than every 2 weeks". */
export const SEAL_TOP_UP_DAYS = 14
/** Criterion 8: "medium: top-up of 0.3 L; critical: more than 0.3 L". */
export const LOW_OIL_MEDIUM_MAX_L = 0.3
/** Criterion 10: the source wording is ambiguous (see SOURCE-FINDINGS section 7), so this is only indicative. */
export const TEMPERATURE_NORMAL_MAX_C = 50
export const TEMPERATURE_MEDIUM_MAX_C = 75
/** Criterion 9: the report gives the viscosity-loss hours as 0.0005 x operating hours. */
export const OIL_HOURS_FACTOR = 0.0005

export function suggestFromSealLeak(input: { topUpEveryDays?: number | null }): Suggestion | null {
  const days = input.topUpEveryDays
  if (days === undefined) return null
  if (days === null || days <= 0) return { level: 'normal', because: 'No se necesita reponer aceite por este sello.' }
  if (days < SEAL_TOP_UP_DAYS) {
    return { level: 'critical', because: `Se repone aceite cada ${days} días: con más frecuencia que cada 2 semanas.` }
  }
  return { level: 'medium', because: `Se repone aceite cada ${days} días: cada 2 semanas aproximadamente o con menos frecuencia.` }
}

export function suggestFromTopUpLitres(litres: number | undefined): Suggestion | null {
  if (litres === undefined || !Number.isFinite(litres) || litres < 0) return null
  if (litres === 0) return { level: 'normal', because: 'El nivel de aceite está en la mitad del visor.' }
  if (litres <= LOW_OIL_MEDIUM_MAX_L) return { level: 'medium', because: `Se necesita una reposición de ${litres} L (hasta ${LOW_OIL_MEDIUM_MAX_L} L).` }
  return { level: 'critical', because: `Se necesita una reposición de ${litres} L (más de ${LOW_OIL_MEDIUM_MAX_L} L).` }
}

export type OilCondition = 'clean' | 'dark' | 'thin'

export function suggestFromOilCondition(condition: OilCondition | undefined): Suggestion | null {
  switch (condition) {
    case 'clean':
      return { level: 'normal', because: 'El aceite está limpio y con su color normal.' }
    case 'dark':
      return { level: 'medium', because: 'El aceite está oscuro pero todavía es viscoso.' }
    case 'thin':
      return { level: 'critical', because: 'El aceite perdió viscosidad.' }
    default:
      return null
  }
}

/** Report rule: viscosity-loss hours = 0.0005 x operating hours. */
export function oilViscosityReferenceHours(operatingHours: number | undefined): number | null {
  if (operatingHours === undefined || !Number.isFinite(operatingHours) || operatingHours < 0) return null
  return Math.round(operatingHours * OIL_HOURS_FACTOR * 1e6) / 1e6
}

export function suggestFromTemperature(celsius: number | undefined): Suggestion | null {
  if (celsius === undefined || !Number.isFinite(celsius)) return null
  const note = ' La redacción del informe para este criterio es ambigua, por lo que debe tomarse como indicativa.'
  if (celsius <= TEMPERATURE_NORMAL_MAX_C) return { level: 'normal', because: `${celsius} °C es hasta ${TEMPERATURE_NORMAL_MAX_C} °C.${note}` }
  if (celsius <= TEMPERATURE_MEDIUM_MAX_C) return { level: 'medium', because: `${celsius} °C está entre ${TEMPERATURE_NORMAL_MAX_C} y ${TEMPERATURE_MEDIUM_MAX_C} °C.${note}` }
  return { level: 'critical', because: `${celsius} °C supera los ${TEMPERATURE_MEDIUM_MAX_C} °C.${note}` }
}

export type HelperKind = 'seal' | 'litres' | 'oil' | 'temperature'

export interface HelperSpec {
  kind: HelperKind
  question: string
  unit?: string
}

/** Which criteria have a helper question (by criterion code). */
export const HELPERS: Record<string, HelperSpec> = {
  oil_leak_output_seal: { kind: 'seal', question: '¿Cada cuánto se repone aceite por este sello?', unit: 'días' },
  oil_leak_input_seal: { kind: 'seal', question: '¿Cada cuánto se repone aceite por este sello?', unit: 'días' },
  low_oil_level: { kind: 'litres', question: '¿Cuántos litros se necesitan para llegar a la mitad del visor?', unit: 'L' },
  oil_oxidation: { kind: 'oil', question: '¿Cómo se ve el aceite?' },
  operating_temperature: { kind: 'temperature', question: 'Temperatura superficial medida', unit: '°C' },
}

export function helperRawValue(kind: HelperKind, a: Record<string, string>): string {
  switch (kind) {
    case 'seal':
      return a.days ? `Aceite repuesto cada ${a.days} días` : ''
    case 'litres':
      return a.litres ? `Reposición necesaria: ${a.litres} L` : ''
    case 'temperature':
      return a.celsius ? `Medido: ${a.celsius} °C` : ''
    case 'oil': {
      const text = a.condition === 'clean' ? 'Aceite limpio' : a.condition === 'dark' ? 'Aceite oscuro pero viscoso' : a.condition === 'thin' ? 'El aceite perdió viscosidad' : ''
      if (!text) return ''
      return a.hours ? `${text}; ${a.hours} horas de operación` : text
    }
  }
}

export function suggestionFor(kind: HelperKind, a: Record<string, string>): Suggestion | null {
  const num = (t: string | undefined) => (t === undefined || t === '' ? undefined : Number(t))
  switch (kind) {
    case 'seal':
      return a.days === undefined || a.days === '' ? (a.none === '1' ? suggestFromSealLeak({ topUpEveryDays: null }) : null) : suggestFromSealLeak({ topUpEveryDays: Number(a.days) })
    case 'litres':
      return suggestFromTopUpLitres(num(a.litres))
    case 'temperature':
      return suggestFromTemperature(num(a.celsius))
    case 'oil':
      return suggestFromOilCondition(a.condition as OilCondition | undefined)
  }
}
